// ============================================================================
// Wave 3.28 — Super-Admin Company Lifecycle dialogs.
//
//   * <CompanySplitDialog />       — split an existing company into A + B
//   * <CompanyHardDeleteDialog />  — permanently purge a company (with safety
//                                    guardrails: forced backup + typed name)
//
// Both dialogs are intended to be triggered from /super-admin/companies.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Loader2,
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  Split as SplitIcon,
  Trash2,
  Download,
  UserPlus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';


function _todayIST() {
  // YYYY-MM-DD in IST. We compute by shifting "now" by +05:30 then slicing.
  const now = new Date();
  const istMs = now.getTime() + (5 * 60 + 30) * 60 * 1000 - now.getTimezoneOffset() * 60 * 1000;
  return new Date(istMs).toISOString().slice(0, 10);
}


// ─────────────────────────────────────────────────────────────────────────
// Split Company
// ─────────────────────────────────────────────────────────────────────────
export function CompanySplitDialog({ company, open, onOpenChange, onSplit }) {
  const today = useMemo(() => _todayIST(), []);
  const [splitDate, setSplitDate] = useState(today);
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [newGstin, setNewGstin] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [users, setUsers] = useState([]);  // [{email, name, role, password}]
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => {
    setSplitDate(today);
    setNewName('');
    setNewAddress('');
    setNewGstin('');
    setNewPhone('');
    setNewEmail('');
    setUsers([]);
    setBusy(false);
    setResult(null);
  };

  useEffect(() => {
    if (open) reset();
     
  }, [open]);

  const addUserRow = () => {
    setUsers((prev) => [...prev, { email: '', name: '', role: 'company_admin', password: '' }]);
  };
  const updateUserRow = (i, patch) => {
    setUsers((prev) => prev.map((u, j) => (j === i ? { ...u, ...patch } : u)));
  };
  const removeUserRow = (i) => setUsers((prev) => prev.filter((_, j) => j !== i));

  const usersValid = users.every((u) => {
    if (!(u.email || '').trim()) return false;
    // Password is only required when this user is being NEWLY created. We can't
    // know up-front, so we require it when set; backend validates again.
    return true;
  });

  const canSubmit = !!newName.trim() && !!splitDate && usersValid && !busy;

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        split_date_ist: splitDate,
        new_company: {
          name: newName.trim(),
          address: newAddress.trim(),
          gstin: newGstin.trim(),
          phone: newPhone.trim(),
          email: newEmail.trim(),
        },
        new_company_users: users.map((u) => ({
          email: u.email.trim(),
          name: (u.name || '').trim(),
          role: u.role || 'company_admin',
          password: u.password || undefined,
        })),
      };
      const { data } = await api.post(`/admin/companies/${company.id}/split`, payload);
      setResult(data);
      toast.success(`Split complete → ${data.new_company?.name}`);
      onSplit?.();
    } catch (e) {
      toast.error(extractError(e, 'Split failed'));
    } finally {
      setBusy(false);
    }
  };

  const closeBtn = (
    <Button onClick={() => onOpenChange(false)} className="luxury-button" data-testid="split-close-button">
      Close
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="company-split-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SplitIcon size={18} className="text-[var(--ink-navy)]" />
            Split Company — {company?.name}
          </DialogTitle>
          <DialogDescription>
            Clone all data into a new <strong>Company B</strong> (full replica as of the cut-off date), then trim{' '}
            <strong>{company?.name}</strong> to keep only active orders, packets with balance, and goldsmiths /
            vendors with outstanding balances. Money ledgers receive a synthetic opening-balance row.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4">
            {/* Split metadata */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="split-date" className="text-xs">Cut-off date (IST) *</Label>
                <DatePicker
                  value={splitDate}
                  maxDate={today}
                  onChange={(e) => setSplitDate(e.target.value)}
                  data-testid="split-date-input"
                />
                <div className="text-[11px] text-slate-500 mt-1">
                  Everything created on or before this date is treated as historical.
                </div>
              </div>
              <div>
                <Label htmlFor="split-new-name" className="text-xs">New company name *</Label>
                <Input
                  id="split-new-name"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder={`${company?.name || ''} – Archive`}
                  data-testid="split-new-name-input"
                />
              </div>
            </div>

            {/* Profile */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">GSTIN</Label>
                <Input
                  value={newGstin}
                  onChange={(e) => setNewGstin(e.target.value)}
                  data-testid="split-new-gstin"
                />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  data-testid="split-new-phone"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Email</Label>
                <Input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  data-testid="split-new-email"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Address</Label>
                <Textarea
                  value={newAddress}
                  onChange={(e) => setNewAddress(e.target.value)}
                  rows={2}
                  data-testid="split-new-address"
                />
              </div>
            </div>

            {/* New company users */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs">User access for new company</Label>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={addUserRow}
                  data-testid="split-add-user-btn"
                >
                  <UserPlus size={12} className="mr-1.5" /> Add user
                </Button>
              </div>
              <div className="text-[11px] text-slate-500 -mt-1 mb-2">
                Leave empty for Super-Admin-only access. Existing users will be linked; new users need a password.
              </div>
              {users.length === 0 && (
                <div className="rounded-md border border-dashed border-[var(--divider)] py-3 text-center text-[12px] text-slate-500">
                  No users assigned. Only Super Admins will see this company.
                </div>
              )}
              {users.map((u, i) => (
                <div
                  key={i}
                  className="grid grid-cols-12 gap-2 items-end mb-2 rounded-md border border-[var(--divider)] bg-white p-2"
                  data-testid={`split-user-row-${i}`}
                >
                  <div className="col-span-4">
                    <Label className="text-[10px]">Email</Label>
                    <Input
                      value={u.email}
                      onChange={(e) => updateUserRow(i, { email: e.target.value })}
                      data-testid={`split-user-email-${i}`}
                    />
                  </div>
                  <div className="col-span-3">
                    <Label className="text-[10px]">Name</Label>
                    <Input
                      value={u.name}
                      onChange={(e) => updateUserRow(i, { name: e.target.value })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">Role</Label>
                    <Select value={u.role} onValueChange={(v) => updateUserRow(i, { role: v })}>
                      <SelectTrigger data-testid={`split-user-role-${i}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="company_admin">Admin</SelectItem>
                        <SelectItem value="company_user">User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-[10px]">Password</Label>
                    <Input
                      type="password"
                      value={u.password}
                      onChange={(e) => updateUserRow(i, { password: e.target.value })}
                      placeholder="if new"
                    />
                  </div>
                  <div className="col-span-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => removeUserRow(i)}
                      className="text-rose-600 px-2"
                      data-testid={`split-user-remove-${i}`}
                    >
                      <X size={14} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Result panel */}
        {result && (
          <div className="space-y-3" data-testid="split-result-panel">
            <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-3">
              <div className="flex items-center gap-2 text-emerald-800 font-medium text-sm">
                <CheckCircle2 size={14} />
                Split complete
              </div>
              <div className="text-[11px] text-slate-600 mt-1">
                Source: {result.source_company_name} → Destination: {result.new_company?.name} (id{' '}
                <span className="font-mono">{(result.new_company?.id || '').slice(0, 8)}</span>) · cut-off{' '}
                {result.split_date_ist}
              </div>
              <div className="text-[11px] text-slate-600">
                Users created in new company: {result.new_company?.users_created || 0} · linked existing:{' '}
                {result.new_company?.users_linked_existing || 0}
              </div>
            </div>

            <SummaryGrid
              title="Cloned to new company"
              dataMap={result.clone_counts}
              accent="text-emerald-700"
              testid="split-clone-counts"
            />
            <SummaryGrid
              title="Trimmed from source company"
              dataMap={result.trim_summary}
              accent="text-rose-700"
              testid="split-trim-summary"
            />
            <SummaryGrid
              title="Opening balance rows inserted"
              dataMap={result.opening_balance_rows}
              accent="text-amber-700"
              testid="split-opening-balance"
            />
          </div>
        )}

        <DialogFooter>
          {result ? (
            closeBtn
          ) : (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={!canSubmit}
                className="luxury-button"
                data-testid="split-submit-button"
              >
                {busy ? (
                  <>
                    <Loader2 size={14} className="animate-spin mr-1.5" /> Splitting…
                  </>
                ) : (
                  <>
                    <SplitIcon size={14} className="mr-1.5" /> Split company
                  </>
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SummaryGrid({ title, dataMap, accent, testid }) {
  const entries = Object.entries(dataMap || {});
  if (entries.length === 0) return null;
  return (
    <div className="border border-[var(--divider)] rounded-md overflow-hidden">
      <div className="bg-slate-50 px-2 py-1.5 text-[11px] text-slate-600 font-medium">{title}</div>
      <table className="w-full text-xs" data-testid={testid}>
        <tbody>
          {entries.map(([k, v]) => (
            <tr key={k} className="border-t border-[var(--divider)]">
              <td className="px-2 py-1 font-mono text-[11px]">{k}</td>
              <td className={`px-2 py-1 text-right font-mono tabular-nums ${v > 0 ? accent + ' font-medium' : 'text-slate-400'}`}>
                {v}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────
// Hard delete Company
// ─────────────────────────────────────────────────────────────────────────
export function CompanyHardDeleteDialog({ company, open, onOpenChange, onDeleted }) {
  const [backupPwd, setBackupPwd] = useState('');
  const [backupToken, setBackupToken] = useState('');
  const [backupGeneratedAt, setBackupGeneratedAt] = useState(null);
  const [confirmName, setConfirmName] = useState('');
  const [busyBackup, setBusyBackup] = useState(false);
  const [busyDelete, setBusyDelete] = useState(false);

  const reset = () => {
    setBackupPwd('');
    setBackupToken('');
    setBackupGeneratedAt(null);
    setConfirmName('');
    setBusyBackup(false);
    setBusyDelete(false);
  };

  useEffect(() => {
    if (open) reset();
     
  }, [open]);

  const generateBackup = async () => {
    if (!backupPwd || backupPwd.length < 6) {
      toast.error('Backup password must be at least 6 characters');
      return;
    }
    setBusyBackup(true);
    try {
      const { data, headers } = await api.post(
        `/admin/companies/${company.id}/backup`,
        { password: backupPwd, include_images: true },
        { responseType: 'blob' },
      );
      const token =
        headers?.['x-backup-token'] || headers?.['X-Backup-Token'] || '';
      if (!token) {
        toast.error('Backup token missing in response — cannot proceed');
        return;
      }
      const cd = headers?.['content-disposition'] || headers?.['Content-Disposition'] || '';
      let filename = `${company.name || 'company'}-backup.xlsx`;
      const m = /filename="([^"]+)"/.exec(cd);
      if (m) filename = m[1];
      const blob = new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setBackupToken(token);
      setBackupGeneratedAt(new Date());
      toast.success(`Backup downloaded — token valid for 15 minutes`);
    } catch (e) {
      toast.error(extractError(e, 'Backup failed'));
    } finally {
      setBusyBackup(false);
    }
  };

  const canDelete =
    !!backupToken && confirmName.trim() === (company?.name || '').trim() && !busyDelete;

  const doDelete = async () => {
    if (!canDelete) return;
    setBusyDelete(true);
    try {
      const { data } = await api.post(`/admin/companies/${company.id}/hard-delete`, {
        confirm_company_name: confirmName.trim(),
        backup_token: backupToken,
      });
      toast.success(`Company "${data.deleted_company_name}" permanently deleted`);
      onDeleted?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(extractError(e, 'Delete failed'));
    } finally {
      setBusyDelete(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busyBackup && !busyDelete) onOpenChange(v); }}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="company-hard-delete-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <Trash2 size={18} />
            Permanently delete — {company?.name}
          </DialogTitle>
          <DialogDescription>
            This will wipe <strong>every</strong> record for {company?.name} across all collections. The
            action is irreversible. You must generate a fresh encrypted backup first.
          </DialogDescription>
        </DialogHeader>

        {/* Big destructive banner */}
        <div className="rounded-md border border-rose-200 bg-rose-50/70 p-3 space-y-2">
          <div className="flex items-start gap-2 text-rose-800">
            <ShieldAlert size={16} className="mt-0.5 shrink-0" />
            <div className="text-[12px] leading-snug">
              Hard delete bypasses the approval workflow. Audit information is preserved in a
              separate Super-Admin audit log, but all operational data, ledgers, packets, orders,
              users, comments, jewellery, and activity logs scoped to this company are lost.
            </div>
          </div>
        </div>

        {/* Step 1: Backup */}
        <div className="space-y-2">
          <Label className="text-xs flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 text-[10px] font-semibold">1</span>
            Generate mandatory backup
          </Label>
          <div className="flex gap-2">
            <Input
              type="password"
              placeholder="Backup password (min 6 chars)"
              value={backupPwd}
              onChange={(e) => setBackupPwd(e.target.value)}
              disabled={!!backupToken}
              data-testid="hard-delete-backup-password"
              className="flex-1"
            />
            <Button
              size="sm"
              onClick={generateBackup}
              disabled={busyBackup || !!backupToken || backupPwd.length < 6}
              className="luxury-button shrink-0"
              data-testid="hard-delete-generate-backup-btn"
            >
              {busyBackup ? (
                <Loader2 size={12} className="animate-spin mr-1.5" />
              ) : (
                <Download size={12} className="mr-1.5" />
              )}
              {backupToken ? 'Done' : 'Download'}
            </Button>
          </div>
          {backupToken && (
            <div className="text-[11px] text-emerald-700 flex items-center gap-1.5" data-testid="hard-delete-backup-success">
              <CheckCircle2 size={12} /> Backup downloaded · token valid for 15 min ·{' '}
              {backupGeneratedAt ? backupGeneratedAt.toLocaleTimeString('en-IN') : ''}
            </div>
          )}
        </div>

        {/* Step 2: Typed name confirmation */}
        <div className="space-y-1.5">
          <Label htmlFor="hd-confirm-name" className="text-xs flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-200 text-[10px] font-semibold">2</span>
            Type <span className="font-mono px-1 rounded bg-rose-50 border border-rose-200">{company?.name}</span> to confirm
          </Label>
          <Input
            id="hd-confirm-name"
            value={confirmName}
            onChange={(e) => setConfirmName(e.target.value)}
            placeholder={company?.name || ''}
            disabled={!backupToken}
            data-testid="hard-delete-confirm-name"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busyBackup || busyDelete}>
            Cancel
          </Button>
          <Button
            onClick={doDelete}
            disabled={!canDelete}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="hard-delete-submit-button"
          >
            {busyDelete ? (
              <>
                <Loader2 size={14} className="animate-spin mr-1.5" /> Deleting…
              </>
            ) : (
              <>
                <Trash2 size={14} className="mr-1.5" /> Permanently delete
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
